import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@cafe-pos/supabase';

type Client = SupabaseClient<Database>;
export type ClientStat = Database['public']['Functions']['client_provider_stats']['Returns'][number];
export type PosDevice = Database['public']['Tables']['pos_devices']['Row'];

export interface ProviderSnapshot {
  stats: Record<string, ClientStat>;
  devices: Record<string, PosDevice[]>;
  /** Recorded silence in the last 30 days, keyed by client id + device id. */
  offlineMinutes30d: Record<string, number>;
  diagnostics30d: Record<string, number>;
}

const YEREVAN = 'Asia/Yerevan';

function num(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function coerce(row: ClientStat): ClientStat {
  return {
    ...row,
    orders_today: num(row.orders_today),
    orders_7d: num(row.orders_7d),
    orders_30d: num(row.orders_30d),
    open_shifts: num(row.open_shifts),
    force_30d: num(row.force_30d),
    force_system_error_30d: num(row.force_system_error_30d),
    force_mistake_30d: num(row.force_mistake_30d),
    force_free_30d: num(row.force_free_30d),
    force_other_30d: num(row.force_other_30d),
    paid_no_fiscal_30d: num(row.paid_no_fiscal_30d),
    paid_no_payment_30d: num(row.paid_no_payment_30d),
    cashiers: num(row.cashiers),
    dashboard_users: num(row.dashboard_users),
    products: num(row.products),
    categories: num(row.categories),
    ingredients: num(row.ingredients),
    recipes: num(row.recipes),
    loyalty_members: num(row.loyalty_members),
    bookings_month: num(row.bookings_month),
    invoices_month: num(row.invoices_month),
    revenue_30d: num(row.revenue_30d),
  };
}

export function yerevanDay(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: YEREVAN,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** Whole Yerevan calendar days since `iso`. Null when there is no timestamp. */
export function daysSince(iso: string | null, now = new Date()): number | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const a = Date.parse(`${yerevanDay(then)}T00:00:00Z`);
  const b = Date.parse(`${yerevanDay(now)}T00:00:00Z`);
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

export type Freshness = 'today' | 'recent' | 'quiet' | 'never';

export function freshness(iso: string | null, now = new Date()): Freshness {
  const days = daysSince(iso, now);
  if (days === null) return 'never';
  if (days === 0) return 'today';
  if (days <= 3) return 'recent';
  return 'quiet';
}

export function deviceKey(clientId: string, deviceId: string): string {
  return `${clientId}:${deviceId}`;
}

/** Minutes since the last heartbeat, once the register has been quiet for 20 minutes. */
export function ongoingSilenceMinutes(lastSeenAt: string, now = Date.now()): number {
  const seen = new Date(lastSeenAt).getTime();
  if (!Number.isFinite(seen)) return 0;
  const minutes = Math.floor((now - seen) / 60_000);
  return minutes >= 20 ? minutes : 0;
}

function compareVersion(a: string, b: string): number {
  const pa = a.split('.').map((n) => Number(n) || 0);
  const pb = b.split('.').map((n) => Number(n) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const delta = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (delta !== 0) return delta;
  }
  return 0;
}

export function registerSummary(devices: PosDevice[]): {
  versions: string[];
  count: number;
  stale: boolean;
} | null {
  if (devices.length === 0) return null;
  const versions = [...new Set(devices.map((d) => d.app_version))].sort(compareVersion);
  const newest = devices.reduce((a, b) => (a.last_seen_at > b.last_seen_at ? a : b));
  const stale = Date.now() - new Date(newest.last_seen_at).getTime() > 24 * 60 * 60_000;
  return { versions, count: devices.length, stale };
}

async function loadDiagnostics(
  supabase: Client,
  clientIds: string[],
  sinceDay: string,
): Promise<Record<string, number>> {
  const out: Record<string, number> = {};
  await Promise.all(
    clientIds.map(async (id) => {
      const { data, error } = await supabase.storage.from('pos-diagnostics').list(id, {
        limit: 100,
        sortBy: { column: 'name', order: 'desc' },
      });
      if (error || !data) {
        out[id] = 0;
        return;
      }
      out[id] = data.filter((file) => file.name.slice(0, 10) >= sinceDay).length;
    }),
  );
  return out;
}

export async function loadProviderSnapshot(supabase: Client, clientIds: string[]): Promise<ProviderSnapshot> {
  const since = new Date(Date.now() - 30 * 86_400_000).toISOString();
  const [statsRes, devicesRes, gapsRes, diagnostics30d] = await Promise.all([
    supabase.rpc('client_provider_stats'),
    supabase.from('pos_devices').select('*'),
    supabase
      .from('pos_device_gaps')
      .select('client_id, device_id, minutes')
      .gte('started_at', since),
    loadDiagnostics(supabase, clientIds, since.slice(0, 10)),
  ]);

  if (statsRes.error) throw new Error(statsRes.error.message);
  if (devicesRes.error) throw new Error(devicesRes.error.message);
  if (gapsRes.error) throw new Error(gapsRes.error.message);

  const stats: Record<string, ClientStat> = {};
  for (const row of statsRes.data ?? []) stats[row.client_id] = coerce(row);

  const devices: Record<string, PosDevice[]> = {};
  for (const row of devicesRes.data ?? []) {
    (devices[row.client_id] ??= []).push(row);
  }
  for (const list of Object.values(devices)) {
    list.sort((a, b) => b.last_seen_at.localeCompare(a.last_seen_at));
  }

  const offlineMinutes30d: Record<string, number> = {};
  for (const row of gapsRes.data ?? []) {
    const key = deviceKey(row.client_id, row.device_id);
    offlineMinutes30d[key] = (offlineMinutes30d[key] ?? 0) + num(row.minutes);
  }

  return { stats, devices, offlineMinutes30d, diagnostics30d };
}
